import type { Location, Size } from "@expert/shared-types";

import { createImmerStore } from "@expert/state-utils";

import { getIsProd } from "../../../config";

export interface GuidedBrowsingSession {
    url: string;
    deliveryStatus: "sent" | "failed";
    deliveryMethod: "sms" | "email";
    isModalOpen: boolean;
}

interface GuidedBrowsingStore {
    sessions: Record<string, GuidedBrowsingSession>;
    getSession: (sessionId: string) => GuidedBrowsingSession | undefined;
    receiveData: (sessionId: string, data: Omit<GuidedBrowsingSession, "isModalOpen">) => void;
    openModal: (sessionId: string) => void;
    closeModal: (sessionId: string) => void;
    resetSession: (sessionId: string) => void;

    frameLocation: Location;
    setFrameLocation: (location: Location) => void;

    frameSize: Size;
    setFrameSize: (size: Size) => void;
}

const DEFAULT_FRAME_LOCATION: Location = { x: 100, y: 100 };
const DEFAULT_FRAME_SIZE: Size = { width: 500, height: 500 };

/**
 * Per-session guided-browsing store: tracks each session's shared-link delivery
 * (`url`, `deliveryStatus`, `deliveryMethod`) and modal open state, plus the
 * draggable frame's `frameLocation` / `frameSize` (shared across sessions).
 *
 * Read per-field with `useGuidedBrowsingStore((state) => ...)` so consumers don't re-render on unrelated updates.
 *
 * Missing-session reads resolve to `undefined` (a stable primitive); consumers null-check it.
 */
export const useGuidedBrowsingStore = createImmerStore<GuidedBrowsingStore>({
    devtools: { store: "guided-browsing-store", name: "workspace/sales-tools", isProd: getIsProd() },
})((set, get) => ({
    sessions: {},
    getSession: (sessionId): GuidedBrowsingSession | undefined => get().sessions[sessionId],
    receiveData: (sessionId, data) =>
        set((state) => {
            state.sessions[sessionId] = {
                ...data,
                isModalOpen: data.deliveryStatus === "sent",
            };
        }),
    openModal: (sessionId) =>
        set((state) => {
            const session = state.sessions[sessionId];
            if (session) {
                session.isModalOpen = true;
            }
        }),
    closeModal: (sessionId) =>
        set((state) => {
            const session = state.sessions[sessionId];
            if (session) {
                session.isModalOpen = false;
            }
        }),
    resetSession: (sessionId) =>
        set((state) => {
            delete state.sessions[sessionId];
        }),

    frameLocation: DEFAULT_FRAME_LOCATION,
    setFrameLocation: (location) =>
        set((state) => {
            state.frameLocation = location;
        }),

    frameSize: DEFAULT_FRAME_SIZE,
    setFrameSize: (size) =>
        set((state) => {
            state.frameSize = size;
        }),
}));
